import { useTranslation } from 'react-i18next';
import { ChangeEventHandler, FC, useEffect, useState } from 'react';
import DeclarationInput from 'components/ui/composed/declarations/formInput/DeclarationInput';
import DeclarationNumberInput from 'components/ui/composed/declarations/formInput/DeclarationNumberInput';
import DeclarationSelect from 'components/ui/composed/declarations/formSelect/DeclarationSelect';
import useCodelists from 'hooks/useCodelists';
import useTooltips from 'hooks/useTooltips';
import { DeclarationFormCardProps } from 'views/declarations/common/declaration.form.card';
import { getFormikProps, handleToggleHelp } from 'views/declarations/utils/form-utils';
import { validators } from 'views/declarations/uk/export/validations/validations';
import { Events } from 'utils/events.enum';
import NewFormCard, { FormCardContainer } from '../../../../../common/cards/NewFormCard';

const VALUATION_INDICATOR_FIELDS = [
    'valuationIndicator1',
    'valuationIndicator2',
    'valuationIndicator3',
    'valuationIndicator4',
] as const;

const ItemValueDetailsCard: FC<DeclarationFormCardProps> = (props) => {
    const { t } = useTranslation('form');
    const { aisCodelists } = useCodelists();
    const { getH1TooltipsByRefNumberAndField } = useTooltips();

    // The BE still expects 4 separate booleans (valuationIndicator1-4), so this single
    // input only changes how the user edits them - it derives a "0101"-style display
    // string from the 4 fields and splits it back out to the 4 fields on change.
    const valuationIndicatorsFormikProps = VALUATION_INDICATOR_FIELDS.map((field) => getFormikProps(field, props));

    const committedValuationIndicatorsValue = valuationIndicatorsFormikProps
        .map(({ fieldProps }) => (fieldProps?.value ? '1' : '0'))
        .join('');

    const [valuationIndicatorsValue, setValuationIndicatorsValue] = useState(committedValuationIndicatorsValue);
    const [valuationIndicatorsError, setValuationIndicatorsError] = useState<string | undefined>(undefined);

    // Listen to the save event of the product so if the user input is invalid we reset to initial value
    useEffect(() => {
        const subscriberId = window.EventBus.subscribe(Events.TRIGGER_SAVE_PRODUCT, () => {
            setValuationIndicatorsValue(committedValuationIndicatorsValue);
            setValuationIndicatorsError(undefined);
        });
        return () => window.EventBus.unsubscribe(subscriberId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [committedValuationIndicatorsValue]);

    const handleValuationIndicatorsChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const rawValue = e.target.value;
        // Always reflect exactly what the user typed - never force the display back to
        // the last committed value, only surface an error when it's invalid.
        setValuationIndicatorsValue(rawValue);

        const isValid = /^[01]{4}$/.test(rawValue) || rawValue === '';
        if (!isValid) {
            const message = 'Valuation Indicator must be exactly 4 digits, each either 0 or 1.';
            setValuationIndicatorsError(message);
            return;
        }
        setValuationIndicatorsError(undefined);

        valuationIndicatorsFormikProps.forEach(({ fieldHelper }, index) => {
            fieldHelper?.setValue(rawValue[index] === '1'); // sets true or false for the corresponding valuationIndicator field
        });
    };

    return (
        <NewFormCard title={t('Value Details')}>
            <FormCardContainer>
                <DeclarationNumberInput
                    required
                    viewOnly={props.viewOnly}
                    {...getFormikProps(`itemAmount`, props)}
                    refNumber="4.14"
                    label={t('itemAmount')}
                    refTooltip={getH1TooltipsByRefNumberAndField('4.14', t('itemAmount'))}
                    refClicked={(ref) => handleToggleHelp(ref, props)}
                    condensed
                />
                <DeclarationSelect
                    required
                    viewOnly={props.viewOnly}
                    {...getFormikProps(`valuationMethod`, props)}
                    refNumber="4.16"
                    label={t('valuationMethod')}
                    selectOptions={aisCodelists?.valuationMethod}
                    tooltip={getH1TooltipsByRefNumberAndField('4.16', t('valuationMethod'))}
                    refClicked={(ref) => handleToggleHelp(ref, props)}
                    condensed
                    codeValidation={[validators.exact(1)]}
                />
                <DeclarationInput
                    viewOnly={props.viewOnly}
                    value={valuationIndicatorsValue}
                    onChange={handleValuationIndicatorsChange}
                    name="valuationIndicators"
                    refNumber="4.13"
                    label={t('valuationIndicators')}
                    tooltip="Each digit corresponds to a specific condition and must be entered as 0 (does not apply) or 1 (applies)"
                    refClicked={(ref) => handleToggleHelp(ref, props)}
                    condensed
                    fieldMeta={{
                        value: valuationIndicatorsValue,
                        touched: true,
                        initialTouched: false,
                        error: valuationIndicatorsError,
                    }}
                />
            </FormCardContainer>
        </NewFormCard>
    );
};
export default ItemValueDetailsCard;
